/**
 * Builds the submitorder payload for a ServiceFlowConfigurator instance.
 * Same payload shape as the legacy configurator getValues() (consumed by /api/config/submitorder).
 */
import { formatDate } from '../../../../../Foundation/utils';

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function getValue(input, warrantyApiConfig) {
  if (input.type === 'checkbox') {
    return !input.dataset.usevaluefromcheckbox ? input.checked : input.value;
  }

  if (input.type === 'file') {
    const file = input.files && input.files[0];
    return JSON.stringify({
      filename: file ? file.name : '',
      content: file ? await readFileAsDataUrl(file) : '',
      samefile: false,
    });
  }

  if (input.nodeName === 'DIV') {
    return input.getAttribute('value');
  }

  if (input.Datepicker) {
    const selectedDate = input.Datepicker.instance.getDate();
    return selectedDate ? formatDate(selectedDate, warrantyApiConfig?.payloadDateFormat) : '';
  }

  return input.value;
}

export async function buildSubmitPayload({
  fields,
  configuratorId,
  serialNumber,
  warrantyApiConfig,
  action,
  configurationMode = 'new',
}) {
  const values = [];

  for (const configuratorField of fields || []) {
    if (configuratorField.excludeFromSubmit) {
      continue;
    }

    if (typeof configuratorField.getSubmitValue === 'function') {
      values.push({
        FieldId: configuratorField.Id,
        Value: configuratorField.dependency?.hidden ? '' : configuratorField.getSubmitValue(),
      });
      continue;
    }

    const inputs = (configuratorField.inputs || []).filter(
      (input) =>
        !(input.type === 'checkbox' && !input.checked && input.dataset.usevaluefromcheckbox),
    );

    const fieldDataList = await Promise.all(
      inputs.map(async (input) => ({
        FieldId: input.dataset.fieldid,
        Value: configuratorField.dependency?.hidden ? '' : await getValue(input, warrantyApiConfig),
      })),
    );

    fieldDataList.forEach((fieldData) => {
      const existing = values.find((x) => x.FieldId === fieldData.FieldId);
      if (existing) {
        existing.Value = existing.Value + '|' + fieldData.Value;
      } else {
        values.push(fieldData);
      }
    });

    if (!values.some((x) => x.FieldId === configuratorField.Id)) {
      values.push({ FieldId: configuratorField.Id, Value: '' });
    }
  }

  return {
    configuratorId,
    configurationMode,
    action,
    Data: {
      SerialNumber: serialNumber,
      Fields: values,
    },
  };
}
